import React, { useState, useEffect, useRef } from 'react';
import {
  CornerUpLeft,
  Upload,
  FileImage,
  Copy,
  RefreshCw,
  Plus,
  Trash2,
  Loader2,
  Download,
  Save,
  Film,
  ArrowRight,
} from 'lucide-react';
import { generateImage } from '../api/storyboardApi';

// --- 页面 8: 画面编辑页 ---
function FrameEditorPage({ onNavigate, pageData }) {
  
  // 如果从上一页传递了shots数据，使用它；否则使用默认数据
  const initialShots = pageData?.shots || [
    { id: '01', img: 'https://placehold.co/100x60/000000/FFF?text=Shot+1', largeImg: 'https://placehold.co/800x450/000000/FFF?text=Shot+1+Large' },
    { id: '02', img: 'https://placehold.co/100x60/000000/FFF?text=Shot+2', largeImg: 'https://placehold.co/800x450/000000/FFF?text=Shot+2+Large' },
    { id: '03', img: 'https://placehold.co/100x60/000000/FFF?text=Shot+3', largeImg: 'https://placehold.co/800x450/000000/FFF?text=Shot+3+Large' },
    { id: '04', img: 'https://placehold.co/100x60/000000/FFF?text=Shot+4', largeImg: 'https://placehold.co/800x450/000000/FFF?text=Shot+4+Large' },
    { id: '05', img: 'https://placehold.co/100x60/000000/FFF?text=Shot+5', largeImg: 'https://placehold.co/800x450/000000/FFF?text=Shot+5+Large' },
  ];
  
  // 区分缩略图 (img) 和高清图 (largeImg)
  const [shots, setShots] = useState(initialShots);
  
  // 默认选中第一个有图片的shot，或第一个
  const firstShotWithImage = shots.find(s => s.img || s.largeImg);
  const [activeShot, setActiveShot] = useState(firstShotWithImage?.id || shots[0]?.id || '02');

  const handleInsertShot = (index) => {  
    const newShot = {
      id: `new_${Date.now()}`,
      img: null, // 空卡槽
      largeImg: null, // 空卡槽
    };
    const newShots = [...shots];
    newShots.splice(index, 0, newShot);
    setShots(newShots);
    setActiveShot(newShot.id);
  };

  const handleDeleteShot = (shotId, e) => {
    e.stopPropagation();
    const newShots = shots.filter(s => s.id !== shotId);
    if (newShots.length === 0) return;
    setShots(newShots);
    if (activeShot === shotId) {
      const idx = shots.findIndex(s => s.id === shotId);
      const nextIdx = Math.min(idx, newShots.length - 1);
      setActiveShot(newShots[nextIdx].id);
    }
  };

  // 悬浮显示删除按钮（用 state 控制，不依赖 group-hover）
  const [hoveredShotId, setHoveredShotId] = useState(null);
  
  // "添加" 按钮组件
  const AddSlotButton = ({ onClick }) => (
    <div className="relative h-4 flex items-center justify-center group">
      <button
        onClick={onClick}
        className="absolute z-10 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-blue-500"
        style={{ top: '-8px' }} // 使其居中于缝隙
      >
        <Plus className="w-4 h-4" />
      </button>
      <div className="w-full h-0.5 bg-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
    </div>
  );

  // 获取当前选中的 *高清图*
  const currentShot = shots.find(s => s.id === activeShot);
  const currentImageSrc = currentShot?.largeImg || 'https://placehold.co/800x450/000000/FFF?text=Select+a+shot'; // 默认图

  // 画面描述：默认使用生成该图时用的句子（shot.desc），切换镜头时同步更新
  const [descriptionText, setDescriptionText] = useState(currentShot?.desc ?? '');
  useEffect(() => {
    setDescriptionText(currentShot?.desc ?? '');
  }, [activeShot, currentShot?.desc]);

  // 本地上传：更新当前镜头的图片
  const fileInputRef = useRef(null);
  const handleUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setShots(prev =>
        prev.map(s =>
          s.id === activeShot ? { ...s, img: dataUrl, largeImg: dataUrl } : s
        )
      );
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // 保存并生成左边：保存当前分镜状态到 localStorage
  const handleSaveLeft = () => {
    try {
      const data = {
        shots,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('chuangshiai_storyboard', JSON.stringify(data));
      alert('已保存当前分镜');
    } catch (err) {
      console.error('保存失败:', err);
      alert('保存失败');
    }
  };

  const handleContinueToVideo = () => {
    const missing = shots.filter((shot) => !shot.img && !shot.largeImg);
    if (missing.length > 0) {
      alert(`还有 ${missing.length} 个镜头没有画面，请生成或删除后继续。`);
      return;
    }
    onNavigate('video-composer', { ...pageData, shots });
  };

  // 一键下载所有分镜为 zip
  const [isDownloading, setIsDownloading] = useState(false);
  const handleDownloadAll = async () => {
    const shotsWithImages = shots.filter(s => s.img || s.largeImg);
    if (shotsWithImages.length === 0) {
      alert('暂无分镜图片可下载');
      return;
    }
    setIsDownloading(true);
    try {
      const JSZip = window.JSZip;
      if (!JSZip) throw new Error('JSZip 未加载，请刷新页面');
      const zip = new JSZip();
      const fetchAsBlob = async (url) => {
        if (url.startsWith('data:')) {
          const res = await fetch(url);
          return await res.blob();
        }
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error(`Failed to fetch: ${url}`);
        return await res.blob();
      };
      for (let i = 0; i < shotsWithImages.length; i++) {
        const shot = shotsWithImages[i];
        const imgUrl = shot.largeImg || shot.img;
        try {
          const blob = await fetchAsBlob(imgUrl);
          zip.file(`分镜_${i + 1}.png`, blob, { binary: true });
        } catch (err) {
          console.warn(`跳过镜头 ${i + 1}:`, err);
        }
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(content);
      a.download = `分镜_${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('下载失败:', err);
      alert('下载失败，部分图片可能因跨域限制无法获取');
    } finally {
      setIsDownloading(false);
    }
  };

  // 重新生成：根据画面描述生成新图并更新当前镜头
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState(null);
  const handleRegenerate = async () => {
    const prompt = descriptionText?.trim();
    if (!prompt) {
      setRegenerateError('请先填写画面描述');
      return;
    }
    if (!currentShot) return;
    setIsRegenerating(true);
    setRegenerateError(null);
    try {
      const result = await generateImage(prompt, { width: 1024, height: 576 });
      const imageUrl = result?.imageUrl;
      if (!imageUrl) throw new Error('生成失败，未返回图片');
      setShots(prev =>
        prev.map(s =>
          s.id === activeShot
            ? {
                ...s,
                img: imageUrl,
                largeImg: imageUrl,
                desc: prompt,
              }
            : s
        )
      );
    } catch (err) {
      console.error('重新生成失败:', err);
      setRegenerateError(err.message || '生成失败，请稍后重试');
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* 1. 顶部 Header */}
      <div className="flex-shrink-0 bg-[#1a1a1a] p-4 flex justify-between items-center border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => onNavigate('storyboard-result')} // 返回结果页
            className="text-gray-400 hover:text-white"
          >
            <CornerUpLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-white text-lg font-semibold">分镜画面定稿</h1>
            <p className="mt-0.5 text-xs text-gray-500">可逐帧修改、替换或重绘不满意的分镜</p>
          </div>
        </div>
        <div className="flex space-x-1 bg-[#2a2a2e] p-1 rounded-lg">
          <button className="py-1 px-3 text-sm text-gray-400 hover:text-white">
            1. 镜头
          </button>
          <button className="py-1 px-3 bg-gray-700 rounded-md text-sm text-white">
            2. 画面
          </button>
          <button className="py-1 px-3 text-sm text-gray-400 hover:text-white">
            3. 分镜
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSaveLeft} className="py-2 px-3 bg-white/5 hover:bg-white/10 text-gray-300 font-medium rounded-lg transition-colors text-sm flex items-center gap-2 border border-white/10">
            <Save className="w-4 h-4" />
            保存分镜
          </button>
          <button
            onClick={handleDownloadAll}
            disabled={isDownloading}
            className="py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors text-sm flex items-center gap-2"
          >
            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {isDownloading ? '打包中...' : '一键下载所有分镜'}
          </button>
          {pageData?.workflow === 'video' && (
            <button onClick={handleContinueToVideo} className="py-2 px-4 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-lg transition-colors text-sm flex items-center gap-2">
              <Film className="w-4 h-4" />
              生成视频 Snap
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* 2. 主编辑区 (三栏布局) */}
      <div className="flex-1 grid grid-cols-12 gap-4 p-4 overflow-hidden">
        
        {/* 左侧: 镜头列表 */}
        <div className="col-span-2 bg-[#1e1e1e] rounded-lg p-3 space-y-0 overflow-y-auto">
          {shots.map((shot, index) => (
            <div key={shot.id} className="relative">
              <AddSlotButton onClick={() => handleInsertShot(index)} />
              
              <div
                className="relative"
                onMouseEnter={() => setHoveredShotId(shot.id)}
                onMouseLeave={() => setHoveredShotId(null)}
              >
                <button 
                  onClick={() => setActiveShot(shot.id)}
                  className={`w-full rounded-md overflow-hidden border-2 
                    ${activeShot === shot.id ? 'border-blue-500' : 'border-transparent'}
                  `}
                >
                  {shot.img ? (
                    // 有图片的卡槽 (使用 img 缩略图)
                    <>
                      <img src={shot.img} alt={`Shot ${shot.id}`} className="w-full h-auto object-cover" />
                      <span className="block text-xs text-gray-300 bg-gray-800 px-1 py-0.5">
                        {index + 1}
                      </span>
                    </>
                  ) : (
                    // 空卡槽的UI
                    <div className="w-full aspect-[100/60] bg-[#2a2a2e] border-2 border-dashed border-gray-600 rounded-md flex items-center justify-center">
                      <Plus className="w-6 h-6 text-gray-500" />
                    </div>
                  )}
                </button>
                {/* 悬浮时显示删除按钮 */}
                <button
                  onClick={(e) => handleDeleteShot(shot.id, e)}
                  className={`absolute top-1 right-1 w-6 h-6 bg-red-600 hover:bg-red-500 rounded-full flex items-center justify-center text-white transition-opacity duration-200 z-10 ${
                    hoveredShotId === shot.id ? 'opacity-100' : 'opacity-0'
                  }`}
                  title="删除镜头"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
           <AddSlotButton onClick={() => handleInsertShot(shots.length)} />
        </div>

        {/* 中间: 主预览图 */}
        <div className="col-span-7 flex flex-col space-y-3">
          <div className="flex-1 bg-black rounded-lg flex items-center justify-center relative">
            <img 
              src={currentImageSrc} // 显示 *高清图*
              alt="Main preview"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            <button 
              onClick={() => onNavigate('inpainting', { 
                originalImage: currentImageSrc,
                prompt: descriptionText || currentShot?.desc || '',
                shots,
                activeShotId: activeShot,
              })}
              className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-md text-sm"
            >
              AI绘图
            </button>
          </div>
          <div className="flex-shrink-0 h-24 bg-[#1e1e1e] rounded-lg p-2 flex space-x-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUpload}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex flex-col items-center justify-center p-2 rounded-md bg-gray-700 hover:bg-gray-600 space-y-1 w-20"
              title="上传本地图片替换当前镜头"
            >
              <Upload className="w-5 h-5 text-gray-300" />
              <span className="text-xs text-gray-300">上传</span>
            </button>
            <button className="flex flex-col items-center justify-center p-2 rounded-md bg-gray-700 hover:bg-gray-600 space-y-1 w-20">
              <FileImage className="w-5 h-5 text-gray-300" />
              <span className="text-xs text-gray-300">原图</span>
            </button>
            <button className="flex flex-col items-center justify-center p-2 rounded-md bg-gray-700 hover:bg-gray-600 space-y-1 w-20">
              <Copy className="w-5 h-5 text-gray-300" />
              <span className="text-xs text-gray-300">相似图</span>
            </button>
          </div>
        </div>

        {/* 右侧: 设置面板 */}
        <div className="col-span-3 bg-[#1e1e1e] rounded-lg p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-sm font-medium text-white block mb-2">
              画面描述
            </label>
            <textarea
              className="w-full h-32 p-2 bg-[#2a2a2e] border border-gray-600 rounded-md text-gray-200 placeholder-gray-500 text-sm"
              placeholder="请输入画面描述..."
              value={descriptionText}
              onChange={(e) => {
                setDescriptionText(e.target.value);
                if (regenerateError) setRegenerateError(null);
              }}
              disabled={isRegenerating}
            />
            <button type="button" className="text-xs text-blue-400 hover:underline mt-1 block text-left">
              + 反向提示词
            </button>
            {regenerateError && (
              <p className="text-xs text-red-400 mt-1">{regenerateError}</p>
            )}
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-white mb-2">单帧重绘</h4>
            <div className="flex items-center space-x-2">
              <button className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center text-3xl text-gray-400 hover:bg-gray-600">
                <Plus />
              </button>
              <button
                onClick={handleRegenerate}
                disabled={isRegenerating}
                className="py-2 px-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm flex items-center gap-1"
              >
                {isRegenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {isRegenerating ? '生成中...' : '重新生成'}
              </button>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-white mb-2">角色参考</h4>
            <div className="grid grid-cols-3 gap-2">
              <div className="w-16 h-16 bg-gray-700 rounded-lg border-2 border-blue-500"></div>
              <div className="w-16 h-16 bg-gray-700 rounded-lg"></div>
            </div>
          </div>

          <button className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors">
            重置生成
          </button>
        </div>
      </div>
    </div>
  );
}

export default FrameEditorPage;
